import React, { FC, memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectFeeTier } from 'state/manage/selectors'
import { setFeeTier } from 'state/manage/reducer'
import Card, { CardBody } from 'components/Card'
import { FlexDivRowCentered } from 'components/layout/flex'
import InputHeaderRow from 'components/Input/InputHeaderRow'
import InputTitle from 'components/Input/InputTitle'
import Connector from 'containers/Connector'
import { FEE_TIERS } from '@bitly/sdk/constants'

const FeeTierSelector: FC = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const feeTier = useAppSelector(selectFeeTier)
	const { network } = Connector.useContainer()

	const activeFeeTiers = FEE_TIERS[network?.id?.toString()!] || []

	const handleSelectTier = useCallback(
		(tier: number) => {
			dispatch(setFeeTier(tier))
		},
		[dispatch]
	)

	return (
		<CardContainer>
			<Card data-testid="fee-tier-selector">
				<StyledCardBody>
					<ContentContainer>
						<InputHeaderRow
							label={
								<StyledInputTitle>
									{t('manage.list-pair.modal.fee-tier', 'Fee Tier')}
								</StyledInputTitle>
							}
						/>
						<TiersContainer>
							{activeFeeTiers.map((tier: number) => (
								<TierOption
									key={tier}
									active={tier === feeTier}
									onClick={() => handleSelectTier(tier)}
								>
									{tier / 10000}%
								</TierOption>
							))}
							{activeFeeTiers.length === 0 && (
								<NoTiersText>No fee tiers available for this network.</NoTiersText>
							)}
						</TiersContainer>
					</ContentContainer>
				</StyledCardBody>
			</Card>
		</CardContainer>
	)
})

const CardContainer = styled.div`
	display: grid;
	height: 183px;
`

const StyledCardBody = styled(CardBody)`
	padding: 20px 32px;
`

const ContentContainer = styled(FlexDivRowCentered)`
	flex-direction: column;
	align-items: flex-start;
	width: 100%;
`

const StyledInputTitle = styled(InputTitle)`
	text-transform: capitalize;
	text-align: left;
	font-size: 14px;
	font-family: ${(props) => props.theme.fonts.regular};
	line-height: 1.25em;
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	width: 180px;
	padding-left: 20px;
`

const TiersContainer = styled(FlexDivRowCentered)`
	width: 100%;
	justify-content: flex-start;
	gap: 12px;
	flex-wrap: wrap;
	padding-left: 20px;
	margin-top: 10px;
`

const TierOption = styled.div<{ active: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 8px 16px;
	border-radius: 8px;
	font-size: 16px;
	font-family: ${(props) => props.theme.fonts.bold};
	cursor: pointer;
	border: 1px solid
		${(props) =>
			props.active ? props.theme.colors.selectedTheme.yellow : props.theme.colors.selectedTheme.border};
	background: ${(props) =>
		props.active ? props.theme.colors.selectedTheme.button.fillHover : 'transparent'};
	color: ${(props) =>
		props.active
			? props.theme.colors.selectedTheme.button.text.primary
			: props.theme.colors.selectedTheme.text.value};
	transition: all 0.2s ease-in-out;

	&:hover {
		border-color: ${(props) => props.theme.colors.selectedTheme.yellow};
	}
`

const NoTiersText = styled.div`
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	font-size: 14px;
`

export default FeeTierSelector
